type PayloadError = {
  error: {
    status: number;
    data: {
      statusCode: number;
      message: string;
    };
  };
  meta: Record<string, unknown>;
};

export const isNumber = (value: unknown): value is number => typeof value === "number";
export const isString = (value: unknown): value is string => typeof value === "string";
export const isArray = (value: unknown): value is unknown[] => Array.isArray(value);
export const isFunction = (p: unknown): p is (...args: never[]) => never => typeof p === "function";
export const isPayloadErrorWithMessage = (payload: unknown): payload is PayloadError =>
  isObject(payload) &&
  "error" in payload &&
  isObject(payload.error) &&
  "data" in payload.error &&
  isObject(payload.error.data) &&
  "message" in payload.error.data &&
  typeof payload.error.data.message === "string";

export const isStatusCode409 = (payload: unknown): payload is PayloadError =>
  isObject(payload) && isObject(payload.error) && isObject(payload.error.data) && isNumber(payload.error.status) && payload.error.status === 409;
export const isStatusCode401 = (payload: unknown): payload is PayloadError =>
  isObject(payload) && isObject(payload.error) && isObject(payload.error.data) && isNumber(payload.error.status) && payload.error.status === 401;

/**
 * Checks if a value is a plain object.
 * @template T - The object type.
 * @param value - The value to check.
 * @returns True if value is an object (not null, not array).
 */
export const isObject = <T extends Record<string, unknown>>(value: unknown): value is T => typeof value === "object" && value !== null && !isArray(value);

/**
 * Checks if an object has a specific property with optional type guard.
 * @template TKey - The property key type.
 * @template T - The property value type.
 * @param value - The value to check.
 * @param key - The property key to look for.
 * @param guard - Optional type guard to validate the property value.
 * @returns True if the object has the specified property (and passes guard if provided).
 */
export const hasProp = <TKey extends PropertyKey, T = unknown>(value: unknown, key: TKey, guard?: (val: unknown) => val is T): value is Record<TKey, T> => {
  if (!isObject(value)) return false;
  if (!(key in value)) return false;
  if (guard) return guard((value as any)[key]);
  return true;
};
